import { Link } from "@/lib/router-compat";
import { ChevronRight, Phone } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { RelatedPages } from "@/components/RelatedPages";
import { SITE_URL, ORG_ID } from "@/components/schemaConstants";
import { esPageWhenAntidepressantsDontWork } from "./learnDataEsWhenAntidepressants";
import type { LearnPageData } from "../learnShared";

const esPages: LearnPageData[] = [esPageWhenAntidepressantsDontWork];

export const esHubSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalWebPage",
      "@id": `${SITE_URL}/es/learn#webpage`,
      name: "Educación para pacientes — Neuro Wellness TMS Centers of America",
      url: `${SITE_URL}/es/learn`,
      description:
        "Guías en lenguaje claro sobre TMS, SAINT® TMS, TMS acelerada, Spravato®, depresión resistente al tratamiento y cómo se comparan los tratamientos para la depresión.",
      inLanguage: "es-US",
      publisher: { "@id": ORG_ID },
      audience: { "@type": "MedicalAudience", audienceType: "Patient" },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Inicio",
          item: `${SITE_URL}/es`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Educación para pacientes",
          item: `${SITE_URL}/es/learn`,
        },
      ],
    },
  ],
};

const PHONE = "954-827-9793";

const sharedRelated = [
  {
    title: "SAINT® TMS acelerada",
    href: "/es/saint",
    description:
      "El protocolo de 5 días guiado por fMRI que ofrecemos en nuestra clínica",
  },
  {
    title: "¿Qué es la terapia TMS?",
    href: "/es/what-is-saint-tms",
    description:
      "Cómo la estimulación magnética transcraneal funciona para la depresión",
  },
  {
    title: "Costo y seguro",
    href: "/es/saint-tms-cost",
    description:
      "Verificación de beneficios, códigos CPT y opciones de pago directo",
  },
  {
    title: "¿Soy candidato?",
    href: "/es/saint",
    description: "Factores de evaluación para la elegibilidad de TMS y SAINT®",
  },
  {
    title: "Solicitar una consulta",
    href: "/es/saint",
    description: "Reserve una consulta gratuita con nuestro equipo clínico",
  },
];

const LearnHubEs = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-20">
        {/* Hero */}
        <section className="border-b border-border bg-card/30">
          <div className="mx-auto max-w-4xl px-4 py-12 sm:py-16">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-1 text-xs text-muted-foreground">
                <li>
                  <Link to="/es" className="hover:text-accent">
                    Inicio
                  </Link>
                </li>
                <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
                <li className="font-medium text-foreground">
                  Educación para pacientes
                </li>
              </ol>
            </nav>
            <h1 className="font-headings text-3xl font-extrabold leading-tight text-primary sm:text-4xl">
              Educación para pacientes
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-foreground">
              Guías en lenguaje claro sobre TMS, SAINT® TMS, TMS acelerada,
              Spravato®, depresión resistente al tratamiento y cómo se comparan
              estos tratamientos. Cada artículo responde las preguntas que los
              pacientes más hacen — escritas en lenguaje claro, con fuentes
              citadas.
            </p>
          </div>
        </section>

        {/* Article list */}
        <section className="py-8 sm:py-12">
          <div className="mx-auto max-w-4xl px-4">
            <div className="space-y-4">
              {esPages.map((page, i) => (
                <Link
                  key={i}
                  to={page.canonical}
                  className="group block rounded-xl border border-border bg-card p-5 shadow-soft transition-all hover:border-accent/40 hover:shadow-premium sm:p-6"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h2 className="font-headings text-lg font-bold text-primary group-hover:text-accent transition-colors sm:text-xl">
                        {page.h1}
                      </h2>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {page.shortAnswer}
                      </p>
                    </div>
                    <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-accent opacity-50 transition-all group-hover:translate-x-1 group-hover:opacity-100" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-primary to-primary-deep py-12 text-center text-white">
          <div className="mx-auto max-w-2xl px-4">
            <h2 className="font-headings text-2xl font-bold sm:text-3xl">
              ¿Tiene preguntas sobre sus opciones de tratamiento?
            </h2>
            <p className="mt-3 text-white/90">
              Nuestro equipo clínico está aquí para ayudarle. Una consulta
              gratuita es la mejor manera de obtener respuestas específicas para
              su situación.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/es/saint"
                className="inline-flex items-center justify-center rounded-lg bg-accent px-6 py-3 text-sm font-bold text-accent-foreground shadow-glow transition-all hover:opacity-90"
              >
                Solicitar una consulta gratuita
              </Link>
              <a
                href={`tel:${PHONE.replace(/-/g, "")}`}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/30 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-white/10"
              >
                <Phone className="h-4 w-4" />
                Llamar al {PHONE}
              </a>
            </div>
          </div>
        </section>

        <RelatedPages heading="Recursos relacionados" links={sharedRelated} />
      </main>
      <Footer />
    </div>
  );
};

export default LearnHubEs;
